import React, { Component } from "react";
import PropTypes from "prop-types";
import { withUser } from "@unifiedpatents/sso";
import { Card, Table, Button, Tooltip, Checkbox, Divider } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import TimeLine from "../../common/timeline";
import LoadingWrapper from "../../common/loading-wrapper";
import { toDateString, toFilingS3Link } from "../../../helpers/converters";
import { sorterNumberGen, sorterStringGen } from "../../../helpers/table";

const styles = {
  gutter: {
    marginTop: 16
  }
};

class FilingTableWithTimeline extends Component {
  static propTypes = {
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    zipLink: PropTypes.string.isRequired
  };

  state = {
    isSelected: () => undefined,
    selectedFilingKey: null,
    showExhibits: true
  };

  render() {
    const { data, loading, user } = this.props;
    const { isSelected, selectedFilingKey, showExhibits } = this.state;
    const importantFilings = [
      "Petition",
      "Amended Petition",
      "Final Decision",
      "Institution Decision",
      "Joinder Decision",
      "Notice of Filing Date",
      "Rehearing Decision",
      "Motion for Joinder",
      "Motion to Amend",
      "Motion to Terminate",
      "Opposition to Amendment",
      "Request for Adverse Judgment",
      "Request for Rehearing",
      "Preliminary Response",
      "Reply to Opposition"
    ];
    const transformTimelineData = data =>
      data.map(d => ({
        date: d.filing_date,
        label: d.up_type,
        key: d.key
      }));
    const tableHeaders = [
      {
        dataIndex: "docket_number",
        key: "docket_number",
        title: "Paper No.",
        sorter: sorterNumberGen("docket_number"),
        width: 100,
        render: (val, record) =>
          user.status.isLoggedIn || record.type !== "Exhibits" ? (
            toFilingS3Link(val, record)
          ) : (
            <Tooltip title="Please log in to access." placement="right">
              <a>{val}</a>
            </Tooltip>
          )
      },
      {
        dataIndex: "filing_date",
        key: "filing_date",
        title: "Date",
        sorter: sorterStringGen("filing_date"),
        width: 100,
        render: toDateString
      },
      {
        dataIndex: "filing_party",
        key: "filing_party",
        title: "Party",
        sorter: sorterStringGen("filing_party"),
        width: 140
      },
      {
        dataIndex: "up_type",
        key: "up_type",
        title: "Type",
        sorter: sorterStringGen("up_type"),
        width: 200
      },
      {
        dataIndex: "docket_text",
        key: "docket_text",
        title: "Name",
        width: 600
      }
    ];
    const tableScroll = {
      x: 1500,
      y: 300
    };
    const rowClassName = record => (selectedFilingKey === record.key ? "up-antd-table-row-selected" : null);
    const docketsZipDownload = (
      <Tooltip title="Download all docket entries (including exhibits) as a zip file directly from the USPTO." placement="bottomRight">
        <Button icon={<DownloadOutlined />} onClick={this._onDocketsZipDownload}>
          {"Download All"}
        </Button>
      </Tooltip>
    );
    return (
      data.length > 0 && (
        <div>
          <Card title="Timeline">
            <LoadingWrapper loading={loading}>
              <TimeLine data={transformTimelineData(data)} importantLabels={importantFilings} onPointSelection={this._onFilingSelection} isSelected={isSelected} cycleSize={5} numMonths={30} />
            </LoadingWrapper>
          </Card>
          <Card
            title={
              <span>
                {"Docket"}
                <Divider type="vertical" />
                <Checkbox checked={showExhibits} onChange={this._toggleShowExhibits}>
                  {"Show Exhibits"}
                </Checkbox>
              </span>
            }
            style={styles.gutter}
            className="Docket"
            extra={docketsZipDownload}
          >
            <Table
              columns={tableHeaders}
              dataSource={showExhibits ? data : data.filter(d => d.docket_number < 1000)}
              rowClassName={rowClassName}
              onRow={this._onRow}
              loading={loading}
              pagination={false}
              scroll={tableScroll}
              size="small"
              id="docketTable"
            />
          </Card>
        </div>
      )
    );
  }

  _handleScroll = () => {
    let elements = document.getElementsByClassName("up-antd-table-row-selected");
    if (elements[0]) {
      const topPosition = elements[0].offsetTop;
      document.querySelector(".ant-table-body").scrollTop = topPosition;
      document.getElementById("docketTable").scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  };

  _deselectFiling = () => {
    this.setState({
      selectedFilingKey: null,
      isSelected: () => undefined
    });
  };

  _onFilingSelection = selectedFilingKey => {
    if (selectedFilingKey === this.state.selectedFilingKey) {
      this._deselectFiling();
    } else {
      this.setState({ isSelected: d => d.key === selectedFilingKey, selectedFilingKey: selectedFilingKey }, this._handleScroll);
    }
  };

  _onRow = a => {
    if (a && a.key >= 0) {
      return {
        onClick: () => {
          const selectedFilingKey = a.key; // only one row can be selected in this table
          if (selectedFilingKey === this.state.selectedFilingKey) {
            this._deselectFiling();
          } else {
            this.setState({
              isSelected: d => d.key === selectedFilingKey,
              selectedFilingKey: selectedFilingKey
            });
          }
        }
      };
    }
  };

  _onDocketsZipDownload = () => {
    window.open(this.props.zipLink);
  };

  _toggleShowExhibits = e => {
    this.setState({
      showExhibits: e.target.checked
    });
  };
}

export default withUser(FilingTableWithTimeline);
